import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Filter, Eye, CheckCircle, Clock, FileText, XCircle } from 'lucide-react';
import { getAllMDTReports } from '../services/mdtReportService';
import CustomSelect from '../components/Common/CustomSelect';

const Reports: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const navigate = useNavigate();

  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchReports = async () => {
      try {
        setLoading(true);
        setError(null);

        const reportsData = await getAllMDTReports();

        setReports(reportsData);
      } catch (err) {
        console.error(err);
        setError("Failed to load MDT reports");
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, []);


  if (loading) return <p>Loading reports...</p>;
  if (error) return <p>{error}</p>;

  const filteredReports = reports.filter(report => {

    const fullName = `${report?.patient?.forename ?? ""} ${report?.patient?.surname ?? ""}`.toLowerCase();
    const matchesSearch = 
      // report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fullName.includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || report.status === filterStatus;
    
    // Role-based filtering: pending reports only visible to consultants
    // const isVisibleToUser = report.status !== 'pending' || user?.role === 'consultant';

    return matchesSearch && matchesFilter;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'draft':
        return 'bg-gray-100 text-gray-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved':
      case 'draft':
        return <FileText className="h-4 w-4" />;
      case 'rejected':
        return <XCircle className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">MDT Reports</h1>
        <p className="text-gray-600">Manage and review multidisciplinary team reports</p>
      </div>

      {/* Search and Filter */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by patient name "
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input-field pl-10"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <CustomSelect
              variant="filter"
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { value: 'all', label: 'All Status' },
                { value: 'draft', label: 'Draft' },
                { value: 'approved', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
              ]}
              placeholder="All Status"
            />
          </div>
        </div>
      </div>

      {/* Reports List */}
      <div className="space-y-4">
        {filteredReports.map((report) => {
          return (
            <div key={report.id} className="card">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">MDT REPORT: {report?.id}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(report.status)}`}>
                      {getStatusIcon(report.status)}
                      <span className="ml-1">{report.status.replace('_', ' ')}</span>
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                    {/* <div>
                      <p className="text-sm text-gray-600">Patient</p>
                      <p className="font-medium text-gray-900">{report?.patient?.forename} {report?.patient?.surname}</p>
                    </div> */}
                    <div>
                      <p className="text-sm text-gray-600">Generated By : {report?.created_by?.name}</p>
                      {/* <p className="font-medium text-gray-900"></p> */}
                    </div>
                  </div>
                </div>
      
                <div className="flex flex-col items-center h-full gap-1">
                  <button 
                    onClick={() => navigate(`/reports/${report.id}`, {
                      state: { from: "dashboard" }
                    })}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <Eye className="h-4 w-4" />
                    <span>View Report</span>
                  </button>

                  <span className="text-xs text-gray-500">
                    Generated on {new Date(report.createdAt).toLocaleDateString("en-GB")}
                  </span>
                </div>               
              </div>
            </div>
          );
        })}
      </div>

      {filteredReports.length === 0 && (
        <div className="card text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No reports found</h3>
          <p className="text-gray-600">Try adjusting your search or filter criteria.</p>
        </div>
      )}
    </div>
  );
};

export default Reports;
